const API_BASE = '/api';

async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('adminToken');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token && options.auth !== false) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    window.location.href = '/admin/login';
    throw new Error('Unauthorized');
  }

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'API Error');
  return data;
}

export const api = {
  // Auth
  login: (email, password) => apiCall('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }), auth: false }),

  // Restaurants
  getRestaurants: () => apiCall('/restaurants', { auth: false }),
  getRestaurant: (slug) => apiCall(`/restaurants/${slug}`, { auth: false }),

  // Locations (countries -> location cards)
  getLocations: () => apiCall('/locations', { auth: false }),
  getAdminLocations: () => apiCall('/admin/locations'),
  createLocation: (data) => apiCall('/admin/locations', { method: 'POST', body: JSON.stringify(data) }),
  updateLocation: (id, data) => apiCall(`/admin/locations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLocation: (id) => apiCall(`/admin/locations/${id}`, { method: 'DELETE' }),
  reorderLocations: (ids) => apiCall('/admin/locations/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
  createLocationCountry: (data) => apiCall('/admin/location-countries', { method: 'POST', body: JSON.stringify(data) }),
  updateLocationCountry: (id, data) => apiCall(`/admin/location-countries/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLocationCountry: (id) => apiCall(`/admin/location-countries/${id}`, { method: 'DELETE' }),
  reorderLocationCountries: (ids) => apiCall('/admin/location-countries/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
  uploadLocationImage: async (file) => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${API_BASE}/admin/locations/upload-image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Image upload failed');
    return data;
  },

  // Menu
  getCategories: () => apiCall('/categories', { auth: false }),
  getMenu: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/menu${query ? `?${query}` : ''}`, { auth: false });
  },
  getMenuItem: (id) => apiCall(`/menu/${id}`, { auth: false }),
  createMenuItem: (data) => apiCall('/menu', { method: 'POST', body: JSON.stringify(data) }),
  updateMenuItem: (id, data) => apiCall(`/menu/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteMenuItem: (id) => apiCall(`/menu/${id}`, { method: 'DELETE' }),

  // Homepage Content
  getFeaturedDishes: () => apiCall('/featured-dishes', { auth: false }),
  updateFeaturedDishes: (itemKeys) => apiCall('/admin/featured-dishes', { method: 'PUT', body: JSON.stringify({ itemKeys }) }),
  getTestimonials: () => apiCall('/testimonials', { auth: false }),
  getAdminTestimonials: () => apiCall('/admin/testimonials'),
  createTestimonial: (data) => apiCall('/admin/testimonials', { method: 'POST', body: JSON.stringify(data) }),
  updateTestimonial: (id, data) => apiCall(`/admin/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTestimonial: (id) => apiCall(`/admin/testimonials/${id}`, { method: 'DELETE' }),
  getNotificationEmails: () => apiCall('/admin/notification-emails'),
  updateNotificationEmails: (data) => apiCall('/admin/notification-emails', { method: 'PUT', body: JSON.stringify(data) }),
  getOnlineOrderPopupSettings: () => apiCall('/online-order-popup', { auth: false }),
  getAdminOnlineOrderPopupSettings: () => apiCall('/admin/online-order-popup'),
  updateOnlineOrderPopupSettings: (data) => apiCall('/admin/online-order-popup', { method: 'PUT', body: JSON.stringify(data) }),
  getEmailTemplates: () => apiCall('/admin/email-templates'),
  getEmailTemplate: (templateKey) => apiCall(`/admin/email-templates/${templateKey}`),
  updateEmailTemplate: (templateKey, data) => apiCall(`/admin/email-templates/${templateKey}`, { method: 'PUT', body: JSON.stringify(data) }),
  resetEmailTemplate: (templateKey) => apiCall(`/admin/email-templates/${templateKey}/reset`, { method: 'POST' }),
  sendTestEmail: (templateKey, data) => apiCall(`/admin/email-templates/${templateKey}/test`, { method: 'POST', body: JSON.stringify(data) }),
  getEmailLocationSettings: () => apiCall('/admin/email-location-settings'),
  updateEmailLocationSetting: (id, data) => apiCall(`/admin/email-location-settings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Reservations
  getReservations: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/reservations${query ? `?${query}` : ''}`);
  },
  createReservation: (data) => apiCall('/reservations', { method: 'POST', body: JSON.stringify(data), auth: false }),
  findManageReservations: (email, phone) => apiCall('/reservations/manage', { method: 'POST', body: JSON.stringify({ email, phone }), auth: false }),
  updateManagedReservation: (id, data) => apiCall(`/reservations/manage/${id}`, { method: 'PUT', body: JSON.stringify(data), auth: false }),
  updateReservation: (id, data) => apiCall(`/reservations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  reservationAction: (id, action, extra = {}) => apiCall(`/admin/reservations/${id}/action`, { method: 'POST', body: JSON.stringify({ action, ...extra }) }),
  deleteReservation: (id) => apiCall(`/reservations/${id}`, { method: 'DELETE' }),

  // Catering
  submitCatering: (data) => apiCall('/catering', { method: 'POST', body: JSON.stringify(data), auth: false }),
  getCateringRequests: () => apiCall('/catering'),
  updateCateringRequest: (id, data) => apiCall(`/catering/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCateringRequest: (id) => apiCall(`/catering/${id}`, { method: 'DELETE' }),

  // Catering By Tray
  getCateringByTrayPublic: () => apiCall('/catering-by-tray', { auth: false, cache: 'no-store' }),
  getCateringByTrayOrderSummary: (id) => apiCall(`/catering-by-tray/orders/${id}`, { auth: false, cache: 'no-store' }),
  createCateringByTrayOrder: (data) => apiCall('/catering-by-tray/orders', { method: 'POST', body: JSON.stringify(data), auth: false }),
  getCateringByTrayAdmin: () => apiCall('/admin/catering-by-tray', { cache: 'no-store' }),
  getCateringByTrayAdminOrders: () => apiCall('/admin/catering-by-tray/orders', { cache: 'no-store' }),
  uploadCateringByTrayImage: async (file) => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('image', file);
    const response = await fetch(`${API_BASE}/admin/catering-by-tray/upload-image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Image upload failed');
    return data;
  },
  saveCateringByTrayCategory: (data) => apiCall(`/admin/catering-by-tray/categories${data.id ? `/${data.id}` : ''}`, { method: data.id ? 'PUT' : 'POST', body: JSON.stringify(data) }),
  deleteCateringByTrayCategory: (id) => apiCall(`/admin/catering-by-tray/categories/${id}`, { method: 'DELETE' }),
  saveCateringByTrayItem: (data) => apiCall(`/admin/catering-by-tray/items${data.id ? `/${data.id}` : ''}`, { method: data.id ? 'PUT' : 'POST', body: JSON.stringify(data) }),
  deleteCateringByTrayItem: (id) => apiCall(`/admin/catering-by-tray/items/${id}`, { method: 'DELETE' }),
  updateCateringByTrayOrder: (id, data) => apiCall(`/admin/catering-by-tray/orders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateCateringByTraySettings: (data) => apiCall('/admin/catering-by-tray/settings', { method: 'PUT', body: JSON.stringify(data) }),

  // Contact
  submitContact: (data) => apiCall('/contact', { method: 'POST', body: JSON.stringify(data), auth: false }),
  getContactInquiries: () => apiCall('/contact'),
  updateContactInquiry: (id, data) => apiCall(`/contact/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContactInquiry: (id) => apiCall(`/contact/${id}`, { method: 'DELETE' }),

  // Analytics
  getAnalytics: () => apiCall('/analytics/overview'),

  // Reservation Settings
  getReservationSettings: () => apiCall('/reservation-settings/pause-status', { auth: false }),
  toggleReservationPause: (paused) => apiCall('/admin/reservation-settings/pause', {
    method: 'PUT',
    body: JSON.stringify({ reservations_paused: paused }),
  }),
  toggleReservationTimeRestriction: (enabled, paused = false, warningEnabled = false) => apiCall('/admin/reservation-settings/pause', {
    method: 'PUT',
    body: JSON.stringify({
      reservations_paused: paused,
      time_restriction_enabled: enabled,
      reservation_time_warning_enabled: enabled ? false : warningEnabled,
    }),
  }),
  toggleReservationTimeWarning: (enabled, paused = false, restrictionEnabled = false) => apiCall('/admin/reservation-settings/pause', {
    method: 'PUT',
    body: JSON.stringify({
      reservations_paused: paused,
      time_restriction_enabled: enabled ? false : restrictionEnabled,
      reservation_time_warning_enabled: enabled,
    }),
  }),
  // Legacy Tuesday toggle endpoints (kept for compatibility)
  getLegacyReservationSettings: () => apiCall('/reservation-settings', { auth: false }),
  getReservationAvailability: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/reservation-availability${query ? `?${query}` : ''}`, { auth: false });
  },
  updateReservationSettings: (data) => apiCall('/admin/reservation-settings', { method: 'PUT', body: JSON.stringify(data) }),

  // Smart Reservation Calendar
  getCapacitySettings: () => apiCall('/admin/capacity-settings'),
  updateCapacitySettings: (settings) => apiCall('/admin/capacity-settings', { method: 'PUT', body: JSON.stringify({ settings }) }),
  getReservationBlockouts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/admin/reservation-blockouts${query ? `?${query}` : ''}`);
  },
  createReservationBlockouts: (data) => apiCall('/admin/reservation-blockouts', { method: 'POST', body: JSON.stringify(data) }),
  deleteReservationBlockout: (id) => apiCall(`/admin/reservation-blockouts/${id}`, { method: 'DELETE' }),
  getAdminNotifications: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/admin/notifications${query ? `?${query}` : ''}`);
  },
  markAdminNotificationRead: (id) => apiCall(`/admin/notifications/${id}/read`, { method: 'PUT' }),
  markAllAdminNotificationsRead: () => apiCall('/admin/notifications/read-all', { method: 'PUT' }),
  getCalendarSummary: (start, end) => apiCall(`/admin/calendar/summary?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`),

  // Hiring Banner
  getHiringBanner: () => apiCall('/hiring-banner', { auth: false }),
  getAdminHiringBanner: () => apiCall('/admin/hiring-banner'),
  updateHiringBanner: (data) => apiCall('/admin/hiring-banner', { method: 'PUT', body: JSON.stringify(data) }),

  // Hiring Applications
  submitHiringApplication: async (formData) => {
    const response = await fetch('/api/hiring-applications', {
      method: 'POST',
      body: formData, // FormData, no Content-Type header (browser sets boundary)
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to submit application');
    return data;
  },
  getHiringApplications: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiCall(`/admin/hiring-applications${query ? `?${query}` : ''}`);
  },
  deleteHiringApplication: (id) => apiCall(`/admin/hiring-applications/${id}`, { method: 'DELETE' }),
  downloadResume: (id) => {
    const token = localStorage.getItem('adminToken');
    return fetch(`/api/admin/hiring-applications/${id}/resume`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
  },
};

export default api;
